import { Grid, Avatar, IconButton, Tooltip, Box, Stack, Alert } from "@mui/material";
import MDInput from "components/Basics/MDInput";
import MDButton from "components/Basics/MDButton";
import MDTypography from "components/Basics/MDTypography";
import ClearIcon from "@mui/icons-material/Clear";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import LinkIcon from "@mui/icons-material/Link";
import { useState, useEffect } from "react";
import { t } from "i18next";
import { uploadFileToS3, deleteFileFromPath } from "helpers/s3";

const InputUploadImage = ({
	label,
	value,
	onChange,
	item = {},
	path,
	listOfApis = [],
	disabled,
	error,
	InputProps = {},
	...rest
}) => {
	const [fileURL, setFileURL] = useState("");
	const [errorMessage, setErrorMessage] = useState("");

	useEffect(() => {
		if (errorMessage) {
			const timer = setTimeout(() => {
				setErrorMessage("");
			}, 5000);
			return () => clearTimeout(timer);
		}
		return undefined;
	}, [errorMessage]);

	const uploadImage = async file => {
		if (!file?.type.startsWith("image/")) {
			setErrorMessage(t("FORMS.errorNotImage"));
			return;
		}
		const tempAnswerItemID = `temp_${Date.now()}`;
		const uploadResult = await uploadFileToS3(file, tempAnswerItemID, "image", "fr");

		if (uploadResult?.fileObject) {
			onChange(uploadResult.fileObject);
			setFileURL("");
		} else {
			setErrorMessage(t("FORMS.errorInUpload"));
		}
	};

	const handleImageChange = async e => {
		const file = e.target.files?.[0];
		if (file) await uploadImage(file);
	};

	const handleURLImageChange = async () => {
		const controller = new AbortController();
		const timeout = setTimeout(() => controller.abort(), 8000);

		try {
			setErrorMessage("");
			const response = await fetch(fileURL, { signal: controller.signal });
			clearTimeout(timeout);

			const blob = await response.blob();

			if (!blob.type.startsWith("image/")) {
				setErrorMessage(t("FORMS.errorNotImage"));
				return;
			}

			const file = new File([blob], "image_from_url.jpg", { type: blob.type });
			await uploadImage(file);
		} catch (err) {
			setErrorMessage(t("FORMS.errorInUpload"));
		}
	};

	const deleteImage = async () => {
		if (!value?.key) return;
		await deleteFileFromPath({ key: value.key });
		onChange(null);
	};

	return (
		<Grid
			size="grow"
			sx={{
				flexBasis: "100%",
				minWidth: "100%"
			}}
		>
			<MDTypography variant="body2" sx={{ mb: 1, fontWeight: "bold" }}>
				{label}
			</MDTypography>

			{errorMessage && (
				<Alert severity="error" onClose={() => setErrorMessage("")} sx={{ mb: 2 }}>
					{errorMessage}
				</Alert>
			)}

			{value?.url ? (
				<Box sx={{ position: "relative", width: "fit-content" }}>
					<Avatar
						src={value.url}
						sx={{
							width: 100,
							height: 100,
							objectFit: "cover",
							border: "2px solid #f0f0f0"
						}}
					/>
					<Tooltip title={t("FORMS.LABELS.delete")}>
						<IconButton
							size="small"
							onClick={deleteImage}
							sx={{
								position: "absolute",
								top: -8,
								right: -8,
								backgroundColor: "error.main",
								color: "white",
								"&:hover": { backgroundColor: "error.dark" },
								width: 24,
								height: 24
							}}
						>
							<ClearIcon sx={{ fontSize: 16 }} />
						</IconButton>
					</Tooltip>
				</Box>
			) : (
				<Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
					{/* Upload File */}
					<Box
						component="label"
						htmlFor="image-input-upload"
						sx={{
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							justifyContent: "center",
							width: 120,
							height: 120,
							p: 2,
							border: "2px dashed #ccc",
							borderRadius: 2,
							cursor: disabled ? "not-allowed" : "pointer",
							transition: "all 0.2s",
							"&:hover": {
								backgroundColor: "action.hover"
							},
							opacity: disabled ? 0.5 : 1
						}}
					>
						<input
							id="image-input-upload"
							type="file"
							accept="image/*"
							onChange={handleImageChange}
							disabled={disabled}
							style={{ display: "none" }}
							aria-label={t("FORMS.uploadImage")}
						/>
						<Tooltip title={t("FORMS.uploadImage")}>
							<Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
								<CloudUploadIcon sx={{ fontSize: 36 }} />
								<MDTypography variant="caption" sx={{ textAlign: "center", fontSize: "0.65rem" }}>
									{t("FORMS.uploadImage")}
								</MDTypography>
							</Box>
						</Tooltip>
					</Box>

					{/* OR Divider */}
					<MDTypography variant="body2" sx={{ textTransform: "uppercase", fontWeight: 500 }}>
						{t("FORMS.or")}
					</MDTypography>

					{/* URL Input */}
					<Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}>
						<MDInput
							size="small"
							placeholder={t("FORMS.labelUploadImageURL")}
							value={fileURL}
							onChange={e => setFileURL(e.target.value)}
							disabled={disabled}
							fullWidth
						/>
						<MDButton
							color="info"
							variant="contained"
							disabled={!fileURL?.trim() || disabled}
							onClick={handleURLImageChange}
							fullWidth
						>
							<LinkIcon sx={{ mr: 1, fontSize: 18 }} />
							{t("FORMS.uploadImageURL")}
						</MDButton>
					</Box>
				</Stack>
			)}
		</Grid>
	);
};

export default InputUploadImage;
